import { useCallback, useEffect, useRef, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import type { IngredientRow } from '../types'
import { Highlight } from '../search'
import { ComboboxList, useCombobox, type ComboGroup } from './Combobox'

type Props = {
  value: { ingredient_id: number; name: string; default_unit?: string } | null
  onChange: (ing: IngredientRow) => void
  onCreateRequest: (seedName: string) => void
  placeholder?: string
  // Pre-fill the search box (e.g. seeded from a page-level filter).
  initialQuery?: string
  // Clear the current selection. The picker always clears its own query text
  // when the X is tapped; this lets the parent drop the chosen ingredient too.
  onClear?: () => void
  // Reported true when the box holds text that hasn't resolved to a chosen
  // ingredient (so the parent can block Save with a tailored message). The red
  // outline is driven internally; this only surfaces the state for validation.
  onUnmatchedChange?: (unmatched: boolean) => void
  // When true, recipes tagged used_as:ingredient are offered as sub-recipe
  // components alongside ingredients. Only the recipe editor wants this; pantry,
  // shopping and meal-adjust pickers stay ingredient-only.
  includeRecipes?: boolean
}

export function IngredientPicker({ value, onChange, onCreateRequest, placeholder, initialQuery, onClear, onUnmatchedChange, includeRecipes }: Props) {
  const [query, setQuery] = useState(initialQuery ?? '')
  const [open, setOpen] = useState(false)
  // Flag the field red once the user has interacted (blurred). A query seeded
  // from outside (e.g. the /pantry filter) counts as already touched, so a
  // pre-filled search that matches nothing is flagged the moment the dialog
  // opens rather than waiting for a blur.
  const [touched, setTouched] = useState(!!initialQuery)
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimer = useRef<number | null>(null)

  const fetcher = useCallback(async (q: string): Promise<ComboGroup<IngredientRow>[]> => {
    const list = await api.ingredients(q)
    const groups: ComboGroup<IngredientRow>[] = [
      { label: 'Your library', items: list.owned, owned: true },
    ]
    if (includeRecipes) {
      // Recipes tagged used_as:ingredient are offered as sub-recipe components,
      // normalized into the IngredientRow shape with component_recipe_id set so the
      // editor can tell them apart on pick. id is namespaced negative to avoid
      // colliding with real ingredient ids in the combobox's keying.
      const recipeRows: IngredientRow[] = (list.recipes ?? []).map(r => ({
        id: -r.id,
        slug: '',
        owned: true,
        name: r.name,
        // The component line is measured in the sub-recipe's yield unit.
        default_unit: r.yield_unit || 'serving',
        component_recipe_id: r.id,
      }))
      groups.push({ label: 'Recipes (as ingredient)', items: recipeRows })
    }
    groups.push({ label: 'Catalog', items: list.catalog })
    return groups
  }, [includeRecipes])

  const combo = useCombobox<IngredientRow>({
    query, fetcher, active: open,
    // Only an exact (case-insensitive) name match counts as "already exists",
    // so creating a substring name (e.g. "potato" when "mashed potato mix"
    // exists) stays possible.
    isExactMatch: (row, q) => row.name.trim().toLowerCase() === q.toLowerCase(),
    onPick: (row) => {
      onChange(row)
      setOpen(false)
      inputRef.current?.blur()
    },
    onCreateRequest: (seed) => {
      onCreateRequest(seed)
      setOpen(false)
      inputRef.current?.blur()
    },
  })

  // Show the live query when open; otherwise the chosen name, falling back to
  // the seeded/typed query so a pre-filled search stays visible before focus.
  const display = open ? query : (value?.name ?? query)
  const clearable = display.length > 0

  // Unmatched = there's text but no committed ingredient. We report it up for
  // Save validation, but only paint the red outline once the picker is closed
  // (no flashing red while the user is mid-type with the dropdown open).
  const unmatched = !value && query.trim().length > 0
  const showInvalid = unmatched && !open && touched
  useEffect(() => { onUnmatchedChange?.(unmatched) }, [unmatched, onUnmatchedChange])

  const clear = () => {
    setQuery('')
    onClear?.()
    setOpen(true)
    inputRef.current?.focus()
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={display}
        placeholder={placeholder ?? 'Ingredient'}
        onFocus={e => {
          setOpen(true)
          setQuery(value?.name ?? query)
          const el = e.currentTarget
          window.setTimeout(() => el.select(), 0)
        }}
        onBlur={() => {
          setTouched(true)
          blurTimer.current = window.setTimeout(() => setOpen(false), 120)
        }}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setOpen(false)
            inputRef.current?.blur()
            return
          }
          combo.onKeyDown(e)
        }}
        style={{
          ...inputStyle,
          paddingRight: clearable ? 32 : 10,
          ...(showInvalid ? { borderColor: dangerRed, boxShadow: `0 0 0 1px ${dangerRed}` } : {}),
        }}
      />
      {clearable && (
        <button
          type="button"
          aria-label="Clear"
          onMouseDown={e => e.preventDefault()}
          onClick={clear}
          style={clearBtnStyle}
        >×</button>
      )}
      {open && (
        <div
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            background: theme.surface, border: `1px solid ${theme.lineStrong}`,
            borderRadius: theme.radius,
            boxShadow: '0 10px 30px rgba(20,14,8,0.18)',
            zIndex: 30, maxHeight: 240, overflow: 'auto',
          }}
        >
          <ComboboxList<IngredientRow>
            api={combo}
            query={query}
            itemKey={r => r.id}
            emptyHint="Type to search ingredients…"
            renderItem={(r, terms) => (
              <div style={{
                padding: '7px 10px',
                fontFamily: theme.sans, fontSize: 13, color: theme.ink,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              }}>
                <span><Highlight text={r.name} terms={terms}/></span>
                <span style={{
                  fontSize: 10, letterSpacing: 1, textTransform: 'uppercase',
                  color: r.component_recipe_id ? theme.accent : theme.muted, fontWeight: 600,
                }}>
                  {r.component_recipe_id
                    ? 'recipe'
                    : `${r.owned ? 'yours' : ''}${r.owned && r.default_unit ? ' · ' : ''}${r.default_unit ?? ''}`}
                </span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}

// The app's danger red (matches the Delete button outline elsewhere).
const dangerRed = 'oklch(0.42 0.16 28)'

const inputStyle: React.CSSProperties = {
  all: 'unset',
  boxSizing: 'border-box',
  width: '100%',
  padding: '7px 10px',
  borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`,
  background: theme.bg,
  fontFamily: theme.sans,
  fontSize: 13,
  color: theme.ink,
}
const clearBtnStyle: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
  width: 18, height: 18, borderRadius: 9,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: theme.muted, fontSize: 16, lineHeight: 1,
}
